"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, AlertCircle, RefreshCw, ChevronDown } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { cn } from "~/lib/utils";
import { classifyMediaAccessError } from "~/hooks/use-daily-media-errors";
import {
  diagnosticErrorName,
  recordDeviceTestEvent,
  recordMediaOutcome,
  type AttemptReason,
  type DiagnosticPhase,
} from "./diagnostics";

interface MicCheckProps {
  onPass: () => void;
}

const BARS = 12;
const PASS_THRESHOLD = 0.15;
const PASS_DURATION_MS = 1000;

export function MicCheck({ onPass }: MicCheckProps) {
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number>(0);
  const aboveThresholdSinceRef = useRef<number | null>(null);
  const attemptRef = useRef(0);

  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [selectedMic, setSelectedMic] = useState<string>("");
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<{
    type: "permissions" | "not-found" | "in-use" | "unknown";
    message: string;
  } | null>(null);
  const [isStarting, setIsStarting] = useState(true);

  const stopStream = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = 0;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => undefined);
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  const startMic = useCallback(
    async (deviceId?: string, reason: AttemptReason = "initial") => {
      const attempt = ++attemptRef.current;
      const isCurrent = () => attempt === attemptRef.current;
      recordDeviceTestEvent("started", { step: "mic", attempt, reason });
      let phase: DiagnosticPhase = "get_user_media";
      setIsStarting(true);
      setError(null);
      stopStream();
      aboveThresholdSinceRef.current = null;

      try {
        const constraints: MediaStreamConstraints = {
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (!isCurrent()) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;

        phase = "audio_context";
        const audioCtx = new AudioContext();
        audioCtxRef.current = audioCtx;
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.5;
        source.connect(analyser);
        analyserRef.current = analyser;

        phase = "enumerate_devices";
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!isCurrent()) return;
        const audioDevices = devices.filter((d) => d.kind === "audioinput");
        setMics(audioDevices);

        const activeTrack = stream.getAudioTracks()[0];
        if (activeTrack) {
          setSelectedMic(activeTrack.getSettings().deviceId ?? "");
        }
        void recordMediaOutcome(
          "ready",
          {
            step: "mic",
            attempt,
            track_state: activeTrack?.readyState,
            track_enabled: activeTrack?.enabled,
            track_muted: activeTrack?.muted,
            audio_context_state: audioCtx.state,
          },
          "microphone",
          isCurrent,
        );

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const tick = () => {
          if (!isCurrent() || !analyserRef.current) return;
          analyserRef.current.getByteFrequencyData(dataArray);

          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i]!;
          }
          const avg = sum / dataArray.length / 255;
          setLevel(avg);

          if (avg >= PASS_THRESHOLD) {
            if (!aboveThresholdSinceRef.current) {
              aboveThresholdSinceRef.current = Date.now();
            } else if (
              Date.now() - aboveThresholdSinceRef.current >=
              PASS_DURATION_MS
            ) {
              recordDeviceTestEvent("step_completed", {
                step: "mic",
                attempt,
                method: "automatic",
              });
              onPass();
              return;
            }
          } else {
            aboveThresholdSinceRef.current = null;
          }

          animFrameRef.current = requestAnimationFrame(tick);
        };
        animFrameRef.current = requestAnimationFrame(tick);
      } catch (err) {
        if (!isCurrent()) return;
        const classifiedError = classifyMediaAccessError(err, "mic");
        void recordMediaOutcome(
          "failed",
          {
            step: "mic",
            attempt,
            phase,
            error_name: diagnosticErrorName(err),
            error_type: classifiedError.type,
            audio_context_state: audioCtxRef.current?.state,
          },
          "microphone",
          isCurrent,
        );
        stopStream();
        setError({
          type:
            classifiedError.type === "device-in-use"
              ? "in-use"
              : classifiedError.type,
          message: classifiedError.helpText ?? classifiedError.message,
        });
      } finally {
        if (isCurrent()) setIsStarting(false);
      }
    },
    [stopStream, onPass],
  );

  useEffect(() => {
    void startMic();
    return () => {
      attemptRef.current += 1;
      stopStream();
    };
  }, [startMic, stopStream]);

  const handleMicChange = (deviceId: string) => {
    setSelectedMic(deviceId);
    void startMic(deviceId, "device_change");
  };

  const activeBars = Math.round(level * BARS);

  return (
    <div className="space-y-4">
      {error ? (
        <div className="space-y-3">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {error.type === "permissions" &&
                "Ne možemo da pristupimo mikrofonu"}
              {error.type === "not-found" && "Mikrofon nije pronađen"}
              {error.type === "in-use" && "Mikrofon je zauzet"}
              {error.type === "unknown" && "Greška sa mikrofonom"}
            </AlertTitle>
            <AlertDescription className="mt-2">
              {error.message}
            </AlertDescription>
          </Alert>

          <details className="bg-muted/30 rounded-lg border text-sm">
            <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 font-medium">
              <ChevronDown className="h-3.5 w-3.5" />
              Kako da rešim ovaj problem?
            </summary>
            <ul className="text-muted-foreground list-inside list-disc space-y-1.5 px-3 pb-3">
              {error.type === "permissions" && (
                <>
                  <li>
                    Proverite dozvolu za mikrofon u pregledaču za ovaj sajt
                  </li>
                  <li>Proverite i sistemske dozvole uređaja za pregledač</li>
                  <li>Zatvorite tab i otvorite poziv ponovo nakon promene</li>
                </>
              )}
              {error.type === "not-found" && (
                <>
                  <li>Proverite da li je mikrofon povezan sa računarom</li>
                  <li>
                    Ako koristite slušalice sa mikrofonom, proverite da su
                    pravilno povezane
                  </li>
                  <li>
                    Proverite sistemska podešavanja zvuka i izaberite
                    odgovarajući ulazni uređaj
                  </li>
                </>
              )}
              {error.type === "in-use" && (
                <>
                  <li>
                    Zatvorite druge aplikacije koje koriste mikrofon (Zoom,
                    Teams, Meet...)
                  </li>
                  <li>
                    Zatvorite druge tabove u pregledaču koji koriste mikrofon
                  </li>
                  <li>
                    Ako problem i dalje postoji, potpuno zatvorite pregledač ili
                    restartujte uređaj
                  </li>
                </>
              )}
              {error.type === "unknown" && (
                <>
                  <li>Osvežite stranicu</li>
                  <li>Pokušajte u drugom pregledaču (Chrome ili Firefox)</li>
                  <li>Proverite da druga aplikacija ne koristi mikrofon</li>
                </>
              )}
            </ul>
          </details>

          <Button
            variant="outline"
            size="sm"
            onClick={() => void startMic(undefined, "retry")}
            className="w-full"
          >
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Pokušaj ponovo
          </Button>
        </div>
      ) : (
        <>
          <p className="text-muted-foreground text-sm">
            {isStarting ? "Pokretanje mikrofona..." : "Izgovorite nešto..."}
          </p>

          <div className="ph-no-capture flex h-12 items-end gap-1">
            {Array.from({ length: BARS }).map((_, i) => {
              const isActive = i < activeBars;
              const barHeight = ((i + 1) / BARS) * 100;
              return (
                <div
                  key={i}
                  className={cn(
                    "flex-1 rounded-t transition-colors duration-75",
                    isActive ? "bg-green-500" : "bg-muted-foreground/20",
                  )}
                  style={{ height: `${barHeight}%` }}
                />
              );
            })}
          </div>

          {mics.length > 1 && (
            <div className="ph-no-capture">
              <Select value={selectedMic} onValueChange={handleMicChange}>
                <SelectTrigger className="ph-no-capture h-9 text-xs">
                  <Mic className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                  <SelectValue placeholder="Mikrofon" />
                </SelectTrigger>
                <SelectContent className="ph-no-capture">
                  {mics.map((mic) => (
                    <SelectItem key={mic.deviceId} value={mic.deviceId}>
                      {mic.label || "Mikrofon"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button
            variant="outline"
            onClick={() => {
              recordDeviceTestEvent("step_completed", {
                step: "mic",
                attempt: attemptRef.current,
                method: "manual",
              });
              onPass();
            }}
            className="w-full"
          >
            Mikrofon radi
          </Button>
        </>
      )}
    </div>
  );
}
